import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Trash2, Pencil, X, Check, List, ChevronRight,
  Tag, ToggleLeft, ToggleRight, GripVertical,
} from 'lucide-react'
import { masterDataApi } from '@/lib/api'

interface MDList {
  id: number
  slug: string
  name: string
  description: string | null
  is_active: boolean
  sort_order: number
  item_count: number
}

interface MDItem {
  id: number
  value: string
  label: string | null
  sort_order: number
  is_active: boolean
}

export default function MasterDataAttributesPage() {
  const qc = useQueryClient()

  // ── list selection ────────────────────────────────────────────────────
  const [selectedList, setSelectedList] = useState<MDList | null>(null)

  // ── new list form ─────────────────────────────────────────────────────
  const [showNewList, setShowNewList] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [newListDesc, setNewListDesc] = useState('')

  // ── edit list ─────────────────────────────────────────────────────────
  const [editingListId, setEditingListId] = useState<number | null>(null)
  const [editListName, setEditListName] = useState('')

  // ── new item form ─────────────────────────────────────────────────────
  const [newItemValue, setNewItemValue] = useState('')
  const [newItemLabel, setNewItemLabel] = useState('')

  // ── edit item ─────────────────────────────────────────────────────────
  const [editingItemId, setEditingItemId] = useState<number | null>(null)
  const [editItemValue, setEditItemValue] = useState('')
  const [editItemLabel, setEditItemLabel] = useState('')

  // ── queries ───────────────────────────────────────────────────────────
  const { data: listsData, isLoading: listsLoading } = useQuery({
    queryKey: ['master-data-lists'],
    queryFn: () => masterDataApi.getLists(),
    staleTime: 30_000,
  })
  const lists: MDList[] = listsData?.data ?? []

  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ['master-data-items', selectedList?.id],
    queryFn: () => masterDataApi.getItems(selectedList!.id),
    enabled: !!selectedList,
    staleTime: 15_000,
  })
  const items: MDItem[] = itemsData?.data ?? []

  // ── mutations — lists ─────────────────────────────────────────────────
  const createListMut = useMutation({
    mutationFn: (d: { name: string; description?: string }) => masterDataApi.createList(d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-data-lists'] })
      setNewListName(''); setNewListDesc(''); setShowNewList(false)
    },
  })

  const updateListMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      masterDataApi.updateList(id, { name }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['master-data-lists'] })
      if (selectedList?.id === res.data.id) setSelectedList(res.data)
      setEditingListId(null)
    },
  })

  const toggleListMut = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      masterDataApi.updateList(id, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['master-data-lists'] }),
  })

  const deleteListMut = useMutation({
    mutationFn: (id: number) => masterDataApi.deleteList(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['master-data-lists'] })
      if (selectedList?.id === id) setSelectedList(null)
    },
  })

  // ── mutations — items ─────────────────────────────────────────────────
  const createItemMut = useMutation({
    mutationFn: (d: { value: string; label?: string }) =>
      masterDataApi.createItem(selectedList!.id, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-data-items', selectedList?.id] })
      qc.invalidateQueries({ queryKey: ['master-data-lists'] })
      setNewItemValue(''); setNewItemLabel('')
    },
  })

  const updateItemMut = useMutation({
    mutationFn: ({ id, value, label }: { id: number; value: string; label: string }) =>
      masterDataApi.updateItem(selectedList!.id, id, { value, label: label || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-data-items', selectedList?.id] })
      setEditingItemId(null)
    },
  })

  const toggleItemMut = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      masterDataApi.updateItem(selectedList!.id, id, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['master-data-items', selectedList?.id] }),
  })

  const deleteItemMut = useMutation({
    mutationFn: (id: number) => masterDataApi.deleteItem(selectedList!.id, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-data-items', selectedList?.id] })
      qc.invalidateQueries({ queryKey: ['master-data-lists'] })
    },
  })

  // ── handlers ──────────────────────────────────────────────────────────
  function handleCreateList(e: React.FormEvent) {
    e.preventDefault()
    const name = newListName.trim()
    if (!name) return
    createListMut.mutate({ name, description: newListDesc.trim() || undefined })
  }

  function handleCreateItem(e: React.FormEvent) {
    e.preventDefault()
    const value = newItemValue.trim()
    if (!value) return
    createItemMut.mutate({ value, label: newItemLabel.trim() || undefined })
  }

  function startEditList(lst: MDList) {
    setEditingListId(lst.id); setEditListName(lst.name)
  }

  function startEditItem(item: MDItem) {
    setEditingItemId(item.id); setEditItemValue(item.value); setEditItemLabel(item.label ?? '')
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Atributos de Master Data</h1>
        <p className="mt-1 text-sm text-gray-500">
          Crie listas de valores personalizados para usar como opções em campos de formulário.
          O slug gerado automaticamente pode ser referenciado como <em>data_source</em> no FormBuilder.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* ── Left: Lists panel ───────────────────────────────────── */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <List className="h-4 w-4" /> Listas
            </h2>
            <button
              onClick={() => setShowNewList(v => !v)}
              className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> Nova lista
            </button>
          </div>

          {/* New list form */}
          {showNewList && (
            <form onSubmit={handleCreateList} className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex flex-col gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nome da lista *</label>
                <input
                  autoFocus
                  value={newListName}
                  onChange={e => setNewListName(e.target.value)}
                  placeholder="ex: Tipos de Defeito Costura"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descrição (opcional)</label>
                <input
                  value={newListDesc}
                  onChange={e => setNewListDesc(e.target.value)}
                  placeholder="Descrição breve"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={createListMut.isPending || !newListName.trim()}
                  className="flex-1 rounded-lg bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {createListMut.isPending ? 'A criar…' : 'Criar lista'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewList(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Lists */}
          {listsLoading ? (
            <p className="text-sm text-gray-400">A carregar…</p>
          ) : lists.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">
              Nenhuma lista criada ainda.
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {lists.map(lst => (
                <li
                  key={lst.id}
                  onClick={() => { setSelectedList(lst); setEditingItemId(null) }}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-3 cursor-pointer transition-colors ${
                    selectedList?.id === lst.id
                      ? 'border-blue-400 bg-blue-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  } ${!lst.is_active ? 'opacity-50' : ''}`}
                >
                  <Tag className="h-4 w-4 shrink-0 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    {editingListId === lst.id ? (
                      <input
                        autoFocus
                        value={editListName}
                        onChange={e => setEditListName(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => {
                          if (e.key === 'Enter') updateListMut.mutate({ id: lst.id, name: editListName })
                          if (e.key === 'Escape') setEditingListId(null)
                        }}
                        className="w-full rounded border border-blue-300 px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <>
                        <p className="text-sm font-medium text-gray-800 truncate">{lst.name}</p>
                        <p className="text-xs text-gray-400 truncate font-mono">{lst.slug}</p>
                      </>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{lst.item_count}</span>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                    {editingListId === lst.id ? (
                      <>
                        <button
                          onClick={() => updateListMut.mutate({ id: lst.id, name: editListName })}
                          className="p-1 text-green-600 hover:text-green-800"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => setEditingListId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => toggleListMut.mutate({ id: lst.id, is_active: !lst.is_active })}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title={lst.is_active ? 'Desativar' : 'Ativar'}
                        >
                          {lst.is_active
                            ? <ToggleRight className="h-4 w-4 text-blue-500" />
                            : <ToggleLeft className="h-4 w-4" />}
                        </button>
                        <button onClick={() => startEditList(lst)} className="p-1 text-gray-400 hover:text-gray-700">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Eliminar lista "${lst.name}" e todos os seus itens?`))
                              deleteListMut.mutate(lst.id)
                          }}
                          className="p-1 text-red-400 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                  {selectedList?.id === lst.id && <ChevronRight className="h-3.5 w-3.5 text-blue-400 shrink-0" />}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Right: Items panel ──────────────────────────────────── */}
        <div className="lg:col-span-3">
          {!selectedList ? (
            <div className="flex h-full items-center justify-center rounded-xl border-2 border-dashed border-gray-200 p-16 text-sm text-gray-400">
              Selecione uma lista à esquerda para gerir os seus valores.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-700">{selectedList.name}</h2>
                  <p className="text-xs text-gray-400 font-mono">data_source: <strong>{selectedList.slug}</strong></p>
                </div>
              </div>

              {/* New item form */}
              <form onSubmit={handleCreateItem} className="flex items-end gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Valor (guardado) *</label>
                  <input
                    value={newItemValue}
                    onChange={e => setNewItemValue(e.target.value)}
                    placeholder="ex: Defeito de revestimento"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Etiqueta (exibida)</label>
                  <input
                    value={newItemLabel}
                    onChange={e => setNewItemLabel(e.target.value)}
                    placeholder="Igual ao valor se vazio"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={createItemMut.isPending || !newItemValue.trim()}
                  className="flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 shrink-0"
                >
                  <Plus className="h-4 w-4" /> Adicionar
                </button>
              </form>

              {/* Items list */}
              {itemsLoading ? (
                <p className="text-sm text-gray-400">A carregar itens…</p>
              ) : items.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">
                  Sem itens ainda. Adicione o primeiro acima.
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {items.map(item => (
                    <li
                      key={item.id}
                      className={`flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 ${!item.is_active ? 'opacity-50' : ''}`}
                    >
                      <GripVertical className="h-4 w-4 text-gray-300 shrink-0" />

                      {editingItemId === item.id ? (
                        <>
                          <input
                            autoFocus
                            value={editItemValue}
                            onChange={e => setEditItemValue(e.target.value)}
                            placeholder="Valor"
                            className="flex-1 rounded border border-blue-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <input
                            value={editItemLabel}
                            onChange={e => setEditItemLabel(e.target.value)}
                            placeholder="Etiqueta (opcional)"
                            className="flex-1 rounded border border-blue-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </>
                      ) : (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">{item.label ?? item.value}</p>
                          {item.label && item.label !== item.value && (
                            <p className="text-xs text-gray-400 font-mono">{item.value}</p>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-1 shrink-0">
                        {editingItemId === item.id ? (
                          <>
                            <button
                              onClick={() => updateItemMut.mutate({ id: item.id, value: editItemValue, label: editItemLabel })}
                              className="p-1 text-green-600 hover:text-green-800"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button onClick={() => setEditingItemId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => toggleItemMut.mutate({ id: item.id, is_active: !item.is_active })}
                              className="p-1 text-gray-400 hover:text-gray-600"
                              title={item.is_active ? 'Desativar' : 'Ativar'}
                            >
                              {item.is_active
                                ? <ToggleRight className="h-4 w-4 text-blue-500" />
                                : <ToggleLeft className="h-4 w-4" />}
                            </button>
                            <button onClick={() => startEditItem(item)} className="p-1 text-gray-400 hover:text-gray-700">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(`Eliminar "${item.label ?? item.value}"?`))
                                  deleteItemMut.mutate(item.id)
                              }}
                              className="p-1 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
